/**
 * Streamed markdown.
 *
 * Streamdown holds back incomplete syntax mid-stream — so a half-typed `**bold`
 * does not flash as asterisks — and sanitises via rehype-harden. Both are
 * fiddly enough that a hand-rolled renderer would be a liability rather than
 * fewer lines.
 *
 * It is LAZY and preloaded the moment a turn starts: none of the markdown
 * pipeline is needed to paint an empty chat or a meetings list, and by the time
 * the first delta lands `preloadMarkdown()` has resolved, so there is no
 * fallback flash in practice.
 */
import { lazy, memo, Suspense, useMemo, type HTMLAttributes, type ReactNode } from 'react'
import remarkGfm from 'remark-gfm'
import { remarkCitations } from '@/features/chat/remark-citations'
import { cn } from '@/lib/utils'

const Streamdown = lazy(() => import('streamdown').then((m) => ({ default: m.Streamdown })))

/**
 * The reference grammar, without brackets — what `data-cite` carries.
 * Anchored, because this checks a whole attribute rather than searching prose.
 */
const CITE = /^\d{1,9}(?:#\d{1,6}|\.\d{1,7}(?:-\d{1,7})?)?$/

let preloaded: Promise<unknown> | null = null

/** Call as soon as a turn starts — before any text has arrived. */
export function preloadMarkdown(): void {
  preloaded ??= import('streamdown')
}

export const Markdown = memo(function Markdown({
  children,
  streaming = false,
  className,
  renderCitation,
}: {
  children: string
  streaming?: boolean
  className?: string
  /**
   * Renders a `[[12.137]]` marker the model wrote, given its reference without
   * the brackets. Omitted where citations have no meaning — a note body, a
   * template preview — and the markers, if any, stay as written rather than
   * becoming chips that point at nothing.
   */
  renderCitation?: (ref: string) => ReactNode
}) {
  /**
   * `remarkGfm` is listed EXPLICITLY because passing `remarkPlugins` REPLACES
   * Streamdown's default set rather than extending it — and that default is
   * exactly `[remarkGfm]`. Omitting it silently removes GFM from every answer
   * that has citations, so tables render as rows of pipes.
   */
  const plugins = useMemo(
    () => (renderCitation ? [remarkGfm, remarkCitations] : undefined),
    [renderCitation]
  )

  const components = useMemo(
    () =>
      renderCitation
        ? {
            // Keyed on `sup` because that is what survives the sanitiser. A
            // genuine superscript still renders as one: only a marker the
            // plugin produced carries `data-cite`, and only the citation
            // grammar is accepted even then.
            sup: ({ children, ...props }: HTMLAttributes<HTMLElement>) => {
              const raw =
                ((props as Record<string, unknown>)['data-cite'] as string | undefined) ??
                (typeof children === 'string' ? children : undefined)
              if (!raw || !CITE.test(raw)) return <sup>{children}</sup>
              return <>{renderCitation(raw)}</>
            },
          }
        : undefined,
    [renderCitation]
  )

  return (
    <Suspense
      fallback={
        <div className={cn('text-sm whitespace-pre-wrap text-[var(--color-fg)]', className)}>
          {children}
        </div>
      }
    >
      <Streamdown
        mode={streaming ? 'streaming' : 'static'}
        parseIncompleteMarkdown={streaming}
        className={cn('prose-chat', className)}
        // Links in model output go through the safety modal rather than
        // navigating straight out — model-authored URLs are untrusted input.
        linkSafety={{ enabled: true }}
        controls={{ code: true, table: true, mermaid: false }}
        lineNumbers={false}
        remarkPlugins={plugins}
        components={components}
      >
        {children}
      </Streamdown>
    </Suspense>
  )
})
